var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import styles from './heading.styles';
/**
 * @slot default - This heading content
 * @slot before - render passed content before the header
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart heading-wrapper - The component's heading wrapper.
 * @csspart before-wrapper - The component's before slot wrapper.
 * @csspart part-before - The component's before slot.
 */
export class BlocksHeading extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Dynamic tag name for the component
         * 1) This is needed to use proper semantic heading treatments depending on where it lives on the page
         * <cat-text-passage size="sm">
         * <ul>
         * <li>**h1** renders an `h1` tag</li>
         * <li>**h2** renders an `h2` tag. This is the default</li>
         * <li>**h3** renders an `h3` tag</li>
         * <li>**h4** renders an `h4` tag</li>
         * <li>**h5** renders an `h5` tag</li>
         * <li>**h6** renders an `h6` tag</li>
         * </ul>
         * </cat-text-passage>
         */
        this.level = 'h2';
        this.slots = new SlotController(this, null, 'before');
    }
    static { this.styles = styles; }
    get hasSlotBefore() {
        return this.slots.hasSlotted('before');
    }
    render() {
        const componentClassName = this.componentClassNames('cat-c-heading', {
            'cat-c-heading--display-lg': this.variant ? this.variant === 'display-lg' : this.level === 'h1',
            'cat-c-heading--display': this.variant ? this.variant === 'display' : this.level === 'h2',
            'cat-c-heading--headline-lg': this.variant ? this.variant === 'headline-lg' : this.level === 'h3',
            'cat-c-heading--headline': this.variant ? this.variant === 'headline' : this.level === 'h4',
            'cat-c-heading--title': this.variant ? this.variant === 'title' : this.level === 'h5',
            'cat-c-heading--title-sm': this.variant ? this.variant === 'title-sm' : this.level === 'h6',
            'cat-c-heading--display-xl': this.variant === 'display-xl',
            'cat-c-heading--display-sm': this.variant === 'display-sm',
            'cat-c-heading--label-lg': this.variant === 'label-lg',
            'cat-c-heading--label': this.variant === 'label',
            'cat-c-heading--label-sm': this.variant === 'label-sm',
            'cat-c-heading--body': this.variant === 'body',
            'cat-c-heading--body-sm': this.variant === 'body-sm',
            'cat-c-heading--footnote': this.variant === 'footnote',
            'cat-c-heading--footnote-bold': this.variant === 'footnote-bold',
            'cat-c-heading--knockout': this.color === 'knockout',
            'cat-c-heading--knockout-brand': this.color === 'knockout-brand',
            'cat-c-heading--error': this.color === 'error',
            'cat-c-heading--decorator': this.decorator === true,
            'cat-c-heading--disabled': this.isDisabled === true
        });
        switch (this.level) {
            case 'h1':
                return html `
          ${!this.hasSlotBefore ? html `<h1 part="base" class="${componentClassName}"><slot part="part-default"></slot></h1>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h1 part="base" class="${componentClassName}"><slot part="part-default"></slot></h1>
          </div>
        `;
            case 'h2':
                return html `
          ${!this.hasSlotBefore ? html ` <h2 part="base" class="${componentClassName}"><slot part="part-default"></slot></h2>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h2 part="base" class="${componentClassName}"><slot part="part-default"></slot></h2>
          </div>
        `;
            case 'h3':
                return html `
          ${!this.hasSlotBefore ? html ` <h3 part="base" class="${componentClassName}"><slot part="part-default"></slot></h3>` : nothing}
          <div part="base" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h3 part="base" class="${componentClassName}"><slot part="part-default"></slot></h3>
          </div>
        `;
            case 'h4':
                return html `
          ${!this.hasSlotBefore ? html ` <h4 part="base" class="${componentClassName}"><slot part="part-default"></slot></h4> ` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h4 part="base" class="${componentClassName}"><slot part="part-default"></slot></h4>
          </div>
        `;
            case 'h5':
                return html `
          ${!this.hasSlotBefore ? html ` <h5 part="base" class="${componentClassName}"><slot part="part-default"></slot></h5> ` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h5 part="base" class="${componentClassName}"><slot part="part-default"></slot></h5>
          </div>
        `;
            case 'h6':
                return html `
          ${!this.hasSlotBefore ? html ` <h6 part="base" class="${componentClassName}"><slot part="part-default"></slot></h6>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h6 part="base" class="${componentClassName}"><slot part="part-default"></slot></h6>
          </div>
        `;
        }
    }
}
__decorate([
    property({ type: String, reflect: true })
], BlocksHeading.prototype, "variant", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksHeading.prototype, "color", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksHeading.prototype, "level", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksHeading.prototype, "decorator", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksHeading.prototype, "isDisabled", void 0);
if (customElements.get('cat-heading') === undefined) {
    customElements.define('cat-heading', BlocksHeading);
}
